declare global {
    interface HTMLElement {
        _recalculatePositionCallback?: () => void;
        _observer?: MutationObserver;
    }
}

import Vue from 'vue';
import type { DirectiveOptions, DirectiveBinding } from 'vue/types/options';

const wrapperClass: string = '.multiselect__content-wrapper';

/**
 * @summary A Vue directive to make a dropdown panel "float" relative to the viewport.
 * @description Solves the common UI problem where a dropdown menu inside a container with `overflow: hidden` or `overflow: scroll` gets clipped.
 * This directive detaches the dropdown panel from the normal document flow by giving it `position: fixed`. It then manually calculates
 * and continuously updates the panel's `top` and `left` properties to ensure it always appears anchored to its parent input,
 * regardless of any parent container's scroll position or clipping. It is specifically designed to work with components
 * that follow the `vue-multiselect` component's class structure.
 *
 * @example
 * <!-- Basic Usage -->
 * <!-- Attach the directive to your VSelect/multiselect component. -->
 * <VSelect v-floating-select ... />
 *
 * @example
 * <!-- Advanced Usage with a Scrolling Parent Container -->
 * <!-- If the select is inside a specific scrollable container (not the whole page),
 *      pass the selector for that container as the directive's value.
 *      This allows the directive to also listen for scroll events on that specific container. -->
 * <div class="my-custom-scroll-container">
 *   <VSelect v-floating-select="'.my-custom-scroll-container'" ... />
 * </div>
 *
 * @param {HTMLElement} el - The multiselect component's root element that the directive is bound to.
 */
const recalculatePosition = (el: HTMLElement): void => {
    const wrapperElement = el.querySelector<HTMLElement>(wrapperClass);

    if (!wrapperElement) {
        return;
    }

    // Get the most up-to-date coordinates of the multiselect component itself.
    const { top, left, height } = el.getBoundingClientRect();

    // Get the dropdown's rendered height to position it correctly (above/below).
    const wrapperHeight: number = wrapperElement.getBoundingClientRect().height;
    const isAbove: boolean = el.classList.contains('multiselect--above');

    // ensure the element is treated as a fixed-position block.
    wrapperElement.style.position = 'fixed';
    wrapperElement.style.bottom = 'auto';

    // Set the width to match the parent multiselect.
    wrapperElement.style.width = `${el.clientWidth}px`;

    // Set the horizontal position to match the parent multiselect's left edge.
    wrapperElement.style.left = `${left}px`;

    // Calculate and set the vertical position.
    const newTop: number = isAbove ? top + 2 - wrapperHeight : top + height;
    wrapperElement.style.top = `${newTop}px`;
};

const directive: DirectiveOptions = {
    /**
     * Called once the bound element has been inserted into its parent node's DOM.
     */
    inserted(el: HTMLElement, binding: DirectiveBinding): void {
        const rawValue = binding.value;
        const selector: string | undefined = typeof rawValue === 'string' ? rawValue : undefined;
        const parentElement: HTMLElement | null = selector ? document.querySelector<HTMLElement>(selector) : null;

        const recalculatePositionCallback = () => recalculatePosition(el);

        Vue.nextTick(() => {
            recalculatePositionCallback();
        });

        el._recalculatePositionCallback = recalculatePositionCallback;

        if (parentElement) {
            parentElement.addEventListener('scroll', recalculatePositionCallback);

            const observer = new MutationObserver(recalculatePositionCallback);
            observer.observe(parentElement, { childList: true, subtree: true });

            el._observer = observer;
        }

        document.addEventListener('scroll', recalculatePositionCallback, true);
    },

    componentUpdated(el: HTMLElement): void {
        if (!el.classList.contains('multiselect--active')) {
            return;
        }

        el._recalculatePositionCallback?.();
    },

    unbind(el: HTMLElement, binding: DirectiveBinding): void {
        const rawValue = binding.value;
        const selector: string | undefined = typeof rawValue === 'string' ? rawValue : undefined;
        const parentElement: HTMLElement | null = selector ? document.querySelector<HTMLElement>(selector) : null;

        el._observer?.disconnect();

        if (el._recalculatePositionCallback) {
            if (parentElement) {
                parentElement.removeEventListener('scroll', el._recalculatePositionCallback);
            }
            document.removeEventListener('scroll', el._recalculatePositionCallback, true);
        }

        const wrapperElement = el.querySelector<HTMLElement>(wrapperClass);
        if (wrapperElement) {
            wrapperElement.style.width = '';
            wrapperElement.style.position = '';
            wrapperElement.style.top = '';
            wrapperElement.style.left = '';
            wrapperElement.style.bottom = '';
        }

        delete el._recalculatePositionCallback;
        delete el._observer;
    },
};

Vue.directive('floating-select', directive);
